/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, Card, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TableCell, TextField, IconButton, Chip, Tooltip, InputAdornment, Grid,
  Table, TableBody, TableContainer, TableRow, TablePagination,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import Swal from 'sweetalert2';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../../../api';
import { formKeyHandler } from 'utils/formKeyboard';
import { useListKeyboard, ACTIVE_ROW_SX } from 'hooks/useListKeyboard';

const TEAL = '#00798E';
const TH = { py: 1.5, px: 3, textTransform: 'uppercase', fontSize: '0.625rem', fontWeight: 700, color: 'text.secondary', opacity: 0.7, borderBottom: '1.5px solid #f0f2f5', whiteSpace: 'nowrap', letterSpacing: '0.06em', bgcolor: 'transparent' };
const td = (last) => ({ py: 1.5, px: 3, borderBottom: last ? 'none' : '1px solid rgba(0,0,0,0.06)', fontSize: '0.8125rem' });
const upperSx = { '& .MuiInputBase-input': { textTransform: 'uppercase' } };
const UPPER_FIELDS = new Set(['name', 'short_name', 'address', 'contact_person', 'gst_number', 'drug_license_number']);
const EMPTY_FORM = { name: '', short_name: '', address: '', contact_person: '', phone: '', email: '', gst_number: '', drug_license_number: '' };

function SupplierDialog({ open, onClose, onSaved, initial }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial;

  useEffect(() => {
    setForm(initial ? {
      name: initial.name, short_name: initial.short_name || '', address: initial.address || '',
      contact_person: initial.contact_person || '', phone: initial.phone || '', email: initial.email || '',
      gst_number: initial.gst_number || '', drug_license_number: initial.drug_license_number || '',
    } : EMPTY_FORM);
  }, [initial, open]);

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: UPPER_FIELDS.has(k) ? e.target.value.toUpperCase() : e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) { Swal.fire('Validation', 'Supplier name is required', 'warning'); return; }
    setSaving(true);
    try {
      if (isEdit) await api.pharmacy.updateSupplier(initial.id, form);
      else await api.pharmacy.createSupplier(form);
      Swal.fire({ icon: 'success', title: isEdit ? 'Updated' : 'Created', toast: true, position: 'top-end', showConfirmButton: false, timer: 2000, timerProgressBar: true });
      onSaved(); onClose();
    } catch { /* handled */ } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ color: TEAL, fontWeight: 700, borderBottom: '1px solid #f0f2f5', pb: 1.5 }}>
        {isEdit ? 'Edit Supplier' : 'New Supplier'}
      </DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }} onKeyDown={formKeyHandler(handleSave)}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}><TextField autoFocus fullWidth label="Supplier Name *" value={form.name} onChange={set('name')} sx={upperSx} /></Grid>
          <Grid item xs={12} sm={4}><TextField fullWidth label="Short Name" value={form.short_name} onChange={set('short_name')} sx={upperSx} /></Grid>
          <Grid item xs={12}><TextField fullWidth label="Address" value={form.address} onChange={set('address')} multiline rows={2} sx={upperSx} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Contact Person" value={form.contact_person} onChange={set('contact_person')} sx={upperSx} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Phone" value={form.phone} onChange={set('phone')} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Email" value={form.email} onChange={set('email')} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="GST Number" value={form.gst_number} onChange={set('gst_number')} sx={upperSx} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Drug License Number" value={form.drug_license_number} onChange={set('drug_license_number')} sx={upperSx} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1, borderTop: '1px solid #f0f2f5', gap: 1 }}>
        <MDButton variant="outlined" color="secondary" onClick={onClose}>Cancel</MDButton>
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ background: TEAL, color: 'white', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, px: 3, boxShadow: 'none', '&:hover': { background: '#016476', boxShadow: 'none' } }}>
          {saving ? 'Saving…' : 'Save (Ctrl+↵)'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function SuppliersPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const kb = useListKeyboard({
    rowCount: rows.length,
    enabled: !dialogOpen,
    onOpen: (i) => { if (rows[i]) { setEditing(rows[i]); setDialogOpen(true); } },
    onCreate: () => { setEditing(null); setDialogOpen(true); },
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.pharmacy.listSuppliers({ search: search || undefined, limit: pageSize, offset: page * pageSize });
      setRows(res.data || []); setTotal(res.total || 0);
    } catch { /* handled */ } finally { setLoading(false); }
  }, [search, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggleActive = async (row) => {
    try { await api.pharmacy.updateSupplier(row.id, { is_active: row.is_active === 'Y' ? 'N' : 'Y' }); fetchData(); } catch { /* handled */ }
  };

  return (
    <Card>
      <MDBox mx={2} mt={-3} py={2} px={2} variant="gradient" bgColor="logo" borderRadius="lg" coloredShadow="logo" display="flex" justifyContent="space-between" alignItems="center">
        <MDTypography variant="h6" color="white">Suppliers</MDTypography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => { setEditing(null); setDialogOpen(true); }} sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.6)', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>New Supplier (Alt+N)</Button>
      </MDBox>
      <MDBox p={2}>
        <TextField {...kb.searchProps} size="small" placeholder="Search suppliers…  ( / to focus )" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} sx={{ mb: 2, width: 300 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
        <TableContainer sx={{ boxShadow: 'none' }}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={TH}>Supplier</TableCell>
                <TableCell sx={TH}>Short Name</TableCell>
                <TableCell sx={TH}>GST No.</TableCell>
                <TableCell sx={TH}>Drug Lic. No.</TableCell>
                <TableCell sx={TH}>Phone</TableCell>
                <TableCell sx={TH}>Status</TableCell>
                <TableCell sx={TH}>Actions</TableCell>
              </TableRow>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, border: 'none' }}><CircularProgress size={24} sx={{ color: TEAL }} /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, border: 'none', color: 'text.secondary' }}>No suppliers found</TableCell></TableRow>
              ) : rows.map((row, idx) => {
                const last = idx === rows.length - 1;
                return (
                  <TableRow key={row.id} hover {...kb.getRowProps(idx)} sx={{ '&:hover': { bgcolor: 'rgba(0,121,142,0.04)' }, ...(kb.activeIdx === idx ? ACTIVE_ROW_SX : {}) }}>
                    <TableCell sx={td(last)}><MDTypography variant="caption" color="text" fontWeight="medium">{row.name}</MDTypography></TableCell>
                    <TableCell sx={td(last)}><MDTypography variant="caption" color="text" fontWeight="regular">{row.short_name || '—'}</MDTypography></TableCell>
                    <TableCell sx={td(last)}><MDTypography variant="caption" color="text" fontWeight="regular">{row.gst_number || '—'}</MDTypography></TableCell>
                    <TableCell sx={td(last)}><MDTypography variant="caption" color="text" fontWeight="regular">{row.drug_license_number || '—'}</MDTypography></TableCell>
                    <TableCell sx={td(last)}><MDTypography variant="caption" color="text" fontWeight="regular">{row.phone || '—'}</MDTypography></TableCell>
                    <TableCell sx={td(last)}>
                      <Chip label={row.is_active === 'Y' ? 'Active' : 'Inactive'} size="small" color={row.is_active === 'Y' ? 'success' : 'default'} onClick={() => handleToggleActive(row)} sx={{ cursor: 'pointer' }} />
                    </TableCell>
                    <TableCell sx={td(last)}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(row); setDialogOpen(true); }}><EditIcon fontSize="small" sx={{ color: TEAL }} /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={pageSize} onRowsPerPageChange={e => { setPageSize(+e.target.value); setPage(0); }} rowsPerPageOptions={[10, 20, 50]} />
      </MDBox>
      <SupplierDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={fetchData} initial={editing} />
    </Card>
  );
}

export default function PharmSuppliers() {
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}><SuppliersPanel /></MDBox>
      <Footer />
    </DashboardLayout>
  );
}
